import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { MeasurementTypeApi, MeasurementValueTypeDto, MeasurementValueTypeRequestDto } from "../api/client-axios";
import { ApiError } from "../model/apiError";
import { measurementValueTypeFieldKeys } from "../model/localizedEntities";
import { Problem } from "../model/problem";
import { pruneTranslations } from "../utils/translations";
import { addAsyncCases, apiRejection } from "./apiThunk";
import { apiConfiguration } from "./server";

export interface MeasurementValueTypesState {
    measurementValueTypes?: MeasurementValueTypeDto[],
    selectedMeasurementValueType?: MeasurementValueTypeDto,
    status: "idle" | "loading" | "error" | "adding" | "success" | "auth-error" | "deleting";
    error?: string | Problem;
    code?: number;
}

const initialMeasurementValueTypesState: MeasurementValueTypesState = {
    status: "idle",
    error: ""
}

/**
 * Narrows an edited value type to what the API accepts: the response-only `id`
 * is dropped and translation rows the admin left blank are pruned.
 *
 * `uoM` is carried through untranslated — `MeasurementValueTypeTranslationDto`
 * covers `description` only, since units of measurement are language-neutral.
 */
export function toMeasurementValueTypeRequest(valueType: MeasurementValueTypeDto): MeasurementValueTypeRequestDto {
    const { uoM, description, translations } = pruneTranslations(
        valueType,
        measurementValueTypeFieldKeys
    );
    const request: MeasurementValueTypeRequestDto = { uoM, description };
    return translations == null ? request : { ...request, translations };
}

export const fetchMeasurementValueTypes = createAsyncThunk<MeasurementValueTypeDto[], string, { rejectValue: ApiError }>('measurementValueTypes/fetchMeasurementValueTypes', async (measurementTypeId, thunkApi) => {
    try {
        const api = new MeasurementTypeApi(apiConfiguration());
        const response = await api.apiV1AdministrationMeasurementMeasurementTypeIdValueTypesGet(measurementTypeId);
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const addMeasurementValueType = createAsyncThunk<MeasurementValueTypeDto[], { measurementTypeId: string, measurementValueType: MeasurementValueTypeDto }, { rejectValue: ApiError }>('measurementValueTypes/addMeasurementValueType', async (args, thunkApi) => {
    try {
        const api = new MeasurementTypeApi(apiConfiguration());
        await api.apiV1AdministrationMeasurementMeasurementTypeIdValueTypesPut(args.measurementTypeId, toMeasurementValueTypeRequest(args.measurementValueType));
        const response = await api.apiV1AdministrationMeasurementMeasurementTypeIdValueTypesGet(args.measurementTypeId);
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const updateMeasurementValueType = createAsyncThunk<MeasurementValueTypeDto[], { measurementTypeId: string, measurementValueType: MeasurementValueTypeDto }, { rejectValue: ApiError }>('measurementValueTypes/updateMeasurementValueType', async (args, thunkApi) => {
    try {
        const api = new MeasurementTypeApi(apiConfiguration());
        await api.apiV1AdministrationMeasurementMeasurementTypeIdValueTypesIdPost(args.measurementTypeId, args.measurementValueType.id!, toMeasurementValueTypeRequest(args.measurementValueType));
        const response = await api.apiV1AdministrationMeasurementMeasurementTypeIdValueTypesGet(args.measurementTypeId);
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const deleteMeasurementValueType = createAsyncThunk<MeasurementValueTypeDto[], { measurementTypeId: string, id: string }, { rejectValue: ApiError }>('measurementValueTypes/deleteMeasurementValueType', async (args, thunkApi) => {
    try {
        const api = new MeasurementTypeApi(apiConfiguration());
        await api.apiV1AdministrationMeasurementMeasurementTypeIdValueTypesIdDelete(args.measurementTypeId, args.id);
        const response = await api.apiV1AdministrationMeasurementMeasurementTypeIdValueTypesGet(args.measurementTypeId);
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});


export const selectMeasurementValueType = createAction("measurementValueTypes/selectMeasurementValueType", (valueType?: MeasurementValueTypeDto) => ({ payload: valueType }));
export const clearErrorMeasurementValueType = createAction("measurementValueTypes/clearErrorMeasurementValueType");
export const startAddMeasurementValueType = createAction("measurementValueTypes/startAddMeasurementValueType", (valueType?: MeasurementValueTypeDto) => ({ payload: valueType }));
export const startDeleteMeasurementValueType = createAction("measurementValueTypes/startDeleteMeasurementValueType");


const measurementValueTypesSlice = createSlice({
    name: "measurementValueTypes",
    initialState: initialMeasurementValueTypesState,
    reducers: {},
    extraReducers: builder => {
        addAsyncCases(
            builder,
            [fetchMeasurementValueTypes, addMeasurementValueType, updateMeasurementValueType, deleteMeasurementValueType],
            (state, measurementValueTypes) => {
                state.status = "success";
                state.measurementValueTypes = measurementValueTypes;
            }
        );

        builder
            .addCase(selectMeasurementValueType, (state, action) => {
                state.selectedMeasurementValueType = action.payload;
            })
            .addCase(clearErrorMeasurementValueType, (state) => {
                state.status = 'success';
            })
            .addCase(startAddMeasurementValueType, (state, action) => {
                state.status = 'adding';
                state.selectedMeasurementValueType = action.payload;
            })
            .addCase(startDeleteMeasurementValueType, (state) => {
                state.status = 'deleting';
            });
    }
});

export default measurementValueTypesSlice.reducer;
